import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { mapAccommodationEnquirySession } from './accommodationEnquirySessionMap'
import { mapPropertyRate } from './propertyRatesMapper'

// `channelMarkup` arrives in contract-svc-accommodation 27.3.0; derive it from there once published.
type ServerCheckoutEnquiryItem = definitions['CheckoutEnquiryItemResult'] & {
  channelMarkup?: App.AccommodationCheckoutEnquiryChannelMarkup
}

function mapChannelMarkup(
  channelMarkup: App.AccommodationCheckoutEnquiryChannelMarkup,
): App.AccommodationCheckoutEnquiryChannelMarkup {
  return {
    channelMarkupId: channelMarkup.channelMarkupId,
    ...(channelMarkup.markupId ? { markupId: channelMarkup.markupId } : {}),
    sellTotal: channelMarkup.sellTotal,
    ...(channelMarkup.luxPlusTotal != null ? { luxPlusTotal: channelMarkup.luxPlusTotal } : {}),
    baseSellTotal: channelMarkup.baseSellTotal,
    ...(channelMarkup.baseLuxPlusTotal != null ? { baseLuxPlusTotal: channelMarkup.baseLuxPlusTotal } : {}),
  }
}

/**
 * NB: no channel markup is applied to the rate. Checkout pricing is reconciled against
 * `session.data.pricing`, which is what svc-order charges, so uplifting the rate here would only
 * desynchronise the displayed breakdown from the charged total. The campaign's current marked
 * prices come separately as `channelMarkup`, for a replacement session after a price change.
 *
 * `relations` on the mapped rate is always empty: it cross-references sibling rate ids, and a
 * checkout enquiry returns a single rate.
 */
export function mapCheckoutEnquiryItem(
  item: ServerCheckoutEnquiryItem,
): App.AccommodationCheckoutEnquiryItem {
  const session = mapAccommodationEnquirySession(item.session)
  return {
    sessionId: item.sessionId,
    outcome: item.outcome,
    session,
    originalSource: item.originalSource,
    ...(item.selectedSource ? { selectedSource: item.selectedSource } : {}),
    ...(item.rate
      ? { rate: mapPropertyRate(item.rate, undefined, session.data.currency, { [item.rate.id]: item.rate }) }
      : {}),
    ...(item.unavailableReason ? { unavailableReason: item.unavailableReason } : {}),
    ...(item.priceChange ? { priceChange: item.priceChange } : {}),
    ...(item.issues?.length ? { issues: item.issues } : {}),
    ...(item.channelMarkup ? { channelMarkup: mapChannelMarkup(item.channelMarkup) } : {}),
  }
}
